﻿class HttpException extends Error {
  constructor(message, response, errorInfo, source) {
    super(message);
    this.response = response;
    this.errorInfo = errorInfo;
    this.source = source;
  }
}

export const ResultTypes = Object.freeze({
  None: 'None',
  Text: 'Text',
  JSON: 'JSON',
  Blob: 'Blob'
});

export class HttpService {
  constructor(serviceUrl) {
    this.serviceUrl = serviceUrl;
    this.HDR_SAME_SITE_ONLY = 'x-same-site-only';
    /* Set this property to true on services who only allow calls from web pages on the same server.
     * This property will cause an 'x-same-site-only' header attribute to be sent to the server.
     * Since this is a custom header CORS will trigger a pre-flight request (OPTIONS) when the
     * request is not from a page from the same origin, effectively blocking all requests from other
     * sites. For this to work, the server must look for the existence of the 'x-same-site-only'
     * header and take appropriate action if the attribute is not present. */
    this.sameSiteOnly = false;
  }

  applySameSiteOnly(options) {
    if (this.sameSiteOnly) {
      if (!options) {
        options = {};
      }
      if (!options.headers) {
        options.headers = {};
      }
      options.headers[this.HDR_SAME_SITE_ONLY] = true;
    }
    return options;
  }

  prepareGetOptions(options) {
    return this.applySameSiteOnly(options);
  }

  preparePutAndPostOptions(options) {
    return this.applySameSiteOnly(options);
  }

  afterGet(response) { }
  afterPutAndPost(response) { }

  async doFetch(url, options, resultType) {
    try {
      if (resultType === ResultTypes.JSON) {
        // Add an 'Accept' header to let the server know we expect a json object as the result:

        // Declarative approach, but probably not as performant:
        //  options = {
        //    ...options,
        //    headers: {
        //      ...options.headers,
        //      'Accept': 'application/json'
        //    }
        //  };
        // More performant approach:
        if (!options) { options = {}; }
        if (!options.headers) { options.headers = {}; }
        options.headers['Accept'] = 'application/json';
      }
      const response = await fetch(url, options);
      const commErrorMsg = 'An error occurred while communicating with the server';
      let result;
      if (resultType === ResultTypes.JSON) {
        const text = await response.text();
        try {
          result = text ? JSON.parse(text) : {};
        } catch (e) { // Unable to parse text into json...
          if (response.ok) {
            throw new HttpException('The response was not valid JSON', response, text, 'HttpService');
          } else {
            throw new HttpException(commErrorMsg + '. Also, the response was not valid JSON', response, text, 'HttpService');
          }
        }
      } else if (resultType === ResultTypes.Text) {
        result = await response.text();
      } else if (resultType === ResultTypes.Blob) {
        result = response.ok ? await response.blob() : await response.text();
      }
      if (!response.ok) {
        let message;
        /* If the response is a RFC 7807-compliant ProblemDetails json object, then the json would
         * look like this (the exception message in the detail property is something we've deciced
         * to do inside the Error500NoLangRegionController's Index() action method):
         *
         * {
         *   "type": "https://tools.ietf.org/html/rfc7231#section-6.6.1",
         *   "title": "An error occurred while processing your request.",
         *   "status": 500,
         *   "detail": "Object reference not set to an instance of an object.",
         *   "traceId": "00-8eac13e950d101b0fb193a8be9dec7ad-37cbe464992090c2-00"
         * }
         *
         */
        if (result.title) {
          if (result.detail) {
            message = `${result.title}. Details: '${result.detail}''`;
          } else {
            message = result.title;
          }
        } else {
          message = result.Message || commErrorMsg;
        }
        throw new HttpException(message, response, result, 'HttpService');
      }
      return { response, result };
    } catch (ex) {
      if (typeof ex.stack === 'string' && ex.stack === '') {
        // Manually assign the stack:
        ex.stack = (new Error()).stack;
      }
      throw ex;
    }
  }

  /**
   * Uses a fetch call to retrieve a response expected to be in json format. Use the options
   * parameter object to specify a fetch method other than GET (default). If the server api returns the
   * text in json format, then the return value of this method will contain that json string. If the
   * json is a simple string value, then the value will have leading and trailing quote characters.
   */
  async fetchJson(relativeUrl, options) {
    options = this.prepareGetOptions(options);
    const { response, result } = await this.doFetch(this.serviceUrl + relativeUrl, options, ResultTypes.JSON);
    this.afterGet(response);
    return result;
  }

  /**
   * Uses a fetch call to retrieve the response expected to be in json format.
   */
  async get(relativeUrl, options) {
    // Note this method is now just a wrapper around fetchJson. New code should call fetchJson directly!
    return await this.fetchJson(relativeUrl, options);
  }

  /**
   * Uses a fetch call to retrieve a response expected to be in plain text format. Use the options
   * parameter object to specify a fetch method other than GET (default). If the server api returns the
   * text in json format, then the return value of this method will contain that json string. If the
   * json is a simple string value, then the value will have leading and trailing quote characters.
   */
  async fetchText(relativeUrl, options) {
    options = this.prepareGetOptions(options);
    const { response, result } = await this.doFetch(this.serviceUrl + relativeUrl, options, ResultTypes.Text);
    this.afterGet(response);
    return result;
  }

  /**
   * Uses a fetch call to retrieve the response expected to be in binary/blob format.
   */
  async getBlob(relativeUrl, options) {
    options = this.prepareGetOptions(options);
    const { response, result } = await this.doFetch(this.serviceUrl + relativeUrl, options, ResultTypes.Blob);
    this.afterGet(response);
    return result;
  }

  /**
   * Uses a fetch call to make a POST request to the server. The method assumes that the response
   * is in json format.
   */
  async post(relativeUrl, headers, body, signal) {
    let options = {
      method: 'POST',
      headers,
      body,
      signal
    };
    options = this.preparePutAndPostOptions(options);
    const { response, result } = await this.doFetch(this.serviceUrl + relativeUrl, options, ResultTypes.JSON);
    this.afterPutAndPost(response);
    return result;
  }

  /**
   * Uses a fetch call to make a POST request to the server. The method assumes that any returned text
   * is in plain text format. If the server api returns the text in json format, then the return value
   * of this method will contain that json string. If the json is a simple string value, then the value
   * will have leading and trailing quote characters.
   */
  async postAndReturnText(relativeUrl, signal) {
    let options = {
      method: 'POST',
      signal
    };
    return await this.fetchText(relativeUrl, options);
  }

  /**
   * Uses a fetch call to make a PUT request to the server. The method assumes that the response
   * is in json format.
   */
  async put(relativeUrl, headers, body, signal) {
    let options = {
      method: 'PUT',
      headers,
      body,
      signal
    };
    options = this.preparePutAndPostOptions(options);
    const { response, result } = await this.doFetch(this.serviceUrl + relativeUrl, options, ResultTypes.JSON);
    this.afterPutAndPost(response);
    return result;
  }

  /**
   * Makes a POST request to the server. The method assumes that response is in json format.
   */
  async postJson(relativeUrl, jsonString, signal) {
    return await this.post(relativeUrl, { 'Content-Type': 'application/json' }, jsonString, signal);
  }

  /**
   * Makes a POST request to the server. The method assumes that response is in json format.
   */
  async postJsonObject(relativeUrl, obj, signal) {
    return await this.postJson(relativeUrl, JSON.stringify(obj), signal);
  }

  async putJson(relativeUrl, jsonString, signal) {
    return await this.put(relativeUrl, { 'Content-Type': 'application/json' }, jsonString, signal);
  }

  async putJsonObject(relativeUrl, obj, signal) {
    return await this.putJson(relativeUrl, JSON.stringify(obj), signal);
  }

}
